import { useEffect, useRef, useState } from "react";
import { call, register, remove } from "../../funcStore";
import { logAction } from "../../api/logApi";
import LiveNowCard from "./LiveNowCard";
import { weightedRandom, getPreferenceMagnitude } from "../../utils/userPreference";

function decideDefaultTags() {
  return weightedRandom(3);
}

const LOW_PREFERENCE_THRESHOLD = 50;

const styles = {
  homeSection: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    maxWidth: "960px",
  },
  homeSectionTitle: {
    margin: 0,
    fontSize: "1.25rem",
  },
  homeSectionTitleRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    flexWrap: "wrap",
  },
  lowPreferenceNotice: {
    margin: 0,
    color: "#383838",
    fontSize: "0.8rem",
    backgroundColor:"pink",
    padding:8
  },
  homeSectionDescription: {
    margin: 0,
    color: "#555",
    fontSize: "0.9rem",
  },
  resultGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "16px",
  },
  showMoreButton: {
    alignSelf: "center",
    marginTop: "8px",
    padding: "10px 24px",
    border: "none",
    borderRadius: "4px",
    background: "#4b3f8f",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
};

function LiveNow() {
  const SCREEN_KEY = "liveNow";

  const STATE_BEFORE_INIT = "beforeInit";
  const STATE_SEARCHING = "searching";
  const STATE_DISPLAY = "display";
  const STATE_EMPTY = "empty";

  const DISPLAY_STEP = 24;

  const [state, setState] = useState(STATE_BEFORE_INIT);
  const [displayCount, setDisplayCount] = useState(DISPLAY_STEP);

  const dataRef = useRef({
    items: [],
    isPreferenceLow: false,
  });

  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      init();
    }
    if (state === STATE_SEARCHING) {
      search();
    }
  }, [state]);

  useEffect(() => {
    return () => {
      remove(`${SCREEN_KEY}.getItem`);
    };
  }, []);

  // from STATE_BEFORE_INIT
  function init() {
    register(`${SCREEN_KEY}.getItem`, (id) =>
      dataRef.current.items.find((item) => item.video_id === id),
    );

    register(`${SCREEN_KEY}.search`,()=>setState(STATE_SEARCHING));
    
    setState(STATE_SEARCHING);
  }

  // from STATE_SEARCHING
  async function search() {
    const tags = decideDefaultTags();
    const items = (await call("app.getLiveNow", tags)) ?? [];

    logAction("live_now_fetch", { tags, resultCount: items.length });

    dataRef.current.items = items;
    dataRef.current.isPreferenceLow =
      getPreferenceMagnitude() < LOW_PREFERENCE_THRESHOLD;
    setDisplayCount(DISPLAY_STEP);

    setState(items.length > 0 ? STATE_DISPLAY : STATE_EMPTY);
  }

  function showMore() {
    setDisplayCount((count) => count + DISPLAY_STEP);
  }

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  return (
    <section style={styles.homeSection}>
      <div style={styles.homeSectionTitleRow}>
        <h2 style={styles.homeSectionTitle}>配信中</h2>

        {state === STATE_DISPLAY && dataRef.current.isPreferenceLow && (
          <div style={styles.lowPreferenceNotice}>
            <div>あなたの好みを判断するための情報がまだ少ないため、表示を最適化できませんでした </div>
            <div>検索やタグをクリックし、好みのVtuberを探してみましょう </div>
            <div>({`最適化割合 ${Math.floor(getPreferenceMagnitude()/LOW_PREFERENCE_THRESHOLD*100)} %`})</div>
          </div>
        )}
      </div>

      {state === STATE_SEARCHING && (
        <p style={styles.homeSectionDescription}>取得中...</p>
      )}

      {state === STATE_EMPTY && (
        <p style={styles.homeSectionDescription}>
          現在配信中のVTuberが見つかりませんでした。
        </p>
      )}

      {state === STATE_DISPLAY && (
        <>
          <div style={styles.resultGrid}>
            {dataRef.current.items.slice(0, displayCount).map((item) => (
              <LiveNowCard
                key={item.video_id}
                parentKey={SCREEN_KEY}
                itemId={item.video_id}
              />
            ))}
          </div>
          {displayCount < dataRef.current.items.length && (
            <button
              type="button"
              style={styles.showMoreButton}
              onClick={showMore}
            >
              もっと見る
            </button>
          )}
        </>
      )}
    </section>
  );
}

export default LiveNow;
